import { useTranslation } from 'react-i18next';

import { X } from 'lucide-react';

import { Button, cn } from '@codes/ui-kit';

import { ConversationList } from './ConversationList';

import type { HelpTab } from '../types';
import type { ChatView } from '@lemoncloud/ssocio-chatbots-api';

interface HelpPanelProps {
    activeTab: HelpTab;
    onTabChange: (tab: HelpTab) => void;
    onClose: () => void;
    conversations: ChatView[];
    onDeleteConversation: (id: string) => void;
    onTogglePinConversation: (id: string) => void;
    onFaqClick: (faqText: string) => void;
    onConversationClick: (conversation: ChatView) => void;
    currentChatId?: string;
    isDisabled?: boolean;
}

export const HelpPanel = ({
    activeTab,
    onTabChange,
    onClose,
    conversations,
    onDeleteConversation,
    onTogglePinConversation,
    onFaqClick,
    onConversationClick,
    currentChatId,
    isDisabled = false,
}: HelpPanelProps) => {
    const { t } = useTranslation(['translation', 'box']);

    const FAQ_ITEMS = [
        t('box:help.faq.what_is_eurekabox'),
        t('box:help.faq.difference_between_eurekacause_and_eurekabox'),
    ];

    return (
        <div className="m-4 mt-0 p-[6px] bg-[#F4F5F5] dark:bg-[#222325] border border-t-0 border-[#EAEAEC] dark:border-[#3A3C40] rounded-b-lg">
            <div className="flex items-center sticky top-0">
                <Button variant="ghost" className="h-auto p-[2px] group" onClick={onClose}>
                    <X className="w-[18px] h-[18px] text-[#9FA2A7] group-hover:text-text transition-colors duration-200" />
                </Button>
                <div className="flex ml-2 space-x-2">
                    <button
                        onClick={() => onTabChange('faq')}
                        className={cn(
                            'relative px-2 pb-[2px] text-xs transition-colors duration-200',
                            activeTab === 'faq' ? 'text-text-800' : 'text-[#BABCC0] dark:text-[#787878]'
                        )}
                    >
                        {t('box:help.tabs.faq')}
                        {activeTab === 'faq' && (
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-[-3px] w-[18px] h-[2px] bg-text-800 rounded-[100px]" />
                        )}
                    </button>

                    <button
                        onClick={() => onTabChange('chat')}
                        className={cn(
                            'relative px-2 pb-[2px] text-xs transition-colors duration-200',
                            activeTab === 'chat' ? 'text-text-800' : 'text-[#BABCC0] dark:text-[#787878]'
                        )}
                    >
                        {t('box:help.tabs.chat_history')}
                        {activeTab === 'chat' && (
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-[-3px] w-[18px] h-[2px] bg-text-800 rounded-[100px]" />
                        )}
                    </button>
                </div>
            </div>

            <div className="max-h-[228px] overflow-auto mt-[9px]">
                {activeTab === 'faq' && (
                    <ul className="flex flex-col space-y-[3px] px-[3px]">
                        {FAQ_ITEMS.map((item, index) => (
                            <li
                                key={index}
                                className="p-[6px] text-xs text-text-800 bg-white dark:bg-[#02060E] rounded-[5px] line-clamp-2 max-h-10 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1a1a1a]"
                                style={{
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                                onClick={() => onFaqClick(item)}
                            >
                                {item}
                            </li>
                        ))}
                    </ul>
                )}

                {activeTab === 'chat' && (
                    <ConversationList
                        conversations={conversations}
                        onDeleteConversation={onDeleteConversation}
                        onTogglePinConversation={onTogglePinConversation}
                        onConversationClick={onConversationClick}
                        currentChatId={currentChatId}
                        isDisabled={isDisabled}
                    />
                )}
            </div>
        </div>
    );
};
